import { useMemo } from 'react';
import {
  type AssignmentDto,
  SLOTS_PER_DAY,
  clipInterval,
  dayEndSlot,
  dayStartSlot,
  formatSlotTime,
  formatTimeRange,
} from '@betreuung/shared';
import { useAppData } from '@/app/appData';
import { useAssignmentFlows } from '@/features/assignment/useAssignmentFlows';
import { personVars } from '@/lib/personColor';
import type { PersonLookup } from '@/lib/persons';
import { de } from '@/i18n/de';
import { selectedSlots, useSlotSelection } from './useSlotSelection';

/** 12px per 30 minutes — 48 slots make the specified 576px column. */
const SLOT_HEIGHT = 12;

/** Labelled every three hours, `00:00` through `24:00`. */
const LABEL_HOURS = [0, 3, 6, 9, 12, 15, 18, 21, 24];

/** Stands in for the free-slot map when nothing may be started. */
const EMPTY_SLOTS: readonly boolean[] = [];

export interface TimelineBodyProps {
  dayIndex: number;
  assignments: readonly AssignmentDto[];
  persons: PersonLookup;
  /** Called after a flow was started, so one modal layer replaces the other. */
  onFlowStarted: () => void;
}

/**
 * Vertical 30-minute grid of one day.
 *
 * Assignments are blocks; the uncovered slots are invisible buttons underneath
 * them, which is what makes "tap empty time, tap again, assign" work without a
 * drag gesture.
 */
export function TimelineBody({
  dayIndex,
  assignments,
  persons,
  onFlowStarted,
}: TimelineBodyProps) {
  const { canWrite } = useAppData();
  const { openCreate, openEdit } = useAssignmentFlows();
  const { selection, tap } = useSlotSelection(dayIndex);
  const dayStart = dayStartSlot(dayIndex);

  const blocks = useMemo(() => {
    const dayEnd = dayEndSlot(dayIndex);
    return assignments.flatMap((assignment) => {
      const clipped = clipInterval(assignment.startSlot, assignment.endSlot, dayStart, dayEnd);
      return clipped ? [{ assignment, ...clipped }] : [];
    });
  }, [assignments, dayIndex, dayStart]);

  const free = useMemo(() => {
    const flags = new Array<boolean>(SLOTS_PER_DAY).fill(true);
    for (const block of blocks) {
      for (let slot = block.fromSlot; slot < block.toSlot; slot += 1) {
        flags[slot - dayStart] = false;
      }
    }
    return flags;
  }, [blocks, dayStart]);

  const selected = selectedSlots(selection);

  return (
    <div
      className="relative ml-11 h-[576px] border-l border-line"
      style={{
        backgroundImage:
          'repeating-linear-gradient(to bottom, transparent, transparent 11px, var(--color-divider) 11px, var(--color-divider) 12px)',
      }}
    >
      {LABEL_HOURS.map((hour) => (
        <span
          key={hour}
          style={{ top: hour * 2 * SLOT_HEIGHT }}
          className="absolute left-[-44px] -translate-y-1.5 font-mono text-[9.5px] font-medium text-fg-hint"
        >
          {formatSlotTime(hour * 2)}
        </span>
      ))}

      {/* Nothing to start here without write access, and 48 invisible focusable
          buttons would be a real cost to anyone navigating by keyboard. */}
      {(canWrite ? free : EMPTY_SLOTS).map((isFree, slotInDay) =>
        isFree ? (
          <button
            key={slotInDay}
            type="button"
            aria-label={formatSlotTime(slotInDay)}
            onClick={() => tap(slotInDay)}
            style={{ top: slotInDay * SLOT_HEIGHT }}
            className="absolute left-2 right-1 h-3"
          />
        ) : null,
      )}

      {blocks.map(({ assignment, fromSlot, toSlot }) => {
        const person = persons.describe(assignment.personId);
        // A block stays a block for a reader; it just stops being a control.
        const Element = canWrite ? 'button' : 'div';
        return (
          <Element
            key={assignment.id}
            {...(canWrite
              ? {
                  type: 'button' as const,
                  onClick: () => {
                    openEdit(assignment);
                    onFlowStarted();
                  },
                }
              : {})}
            style={{
              ...personVars(person.colorIndex),
              top: (fromSlot - dayStart) * SLOT_HEIGHT,
              height: (toSlot - fromSlot) * SLOT_HEIGHT,
            }}
            // `items-start` because a button centres its content by default,
            // which would float the label in the middle of a long block instead
            // of anchoring it at the start time.
            className="person-bg absolute left-2 right-1 flex flex-col items-start overflow-hidden rounded-cell px-2.5 py-1.5 text-left"
          >
            <span className="block text-label font-semibold">{person.name}</span>
            <span className="block font-mono text-meta font-medium opacity-85">
              {formatTimeRange(fromSlot, toSlot)}
            </span>
          </Element>
        );
      })}

      {selected ? (
        <div
          aria-hidden
          style={{
            top: selected.fromSlotInDay * SLOT_HEIGHT,
            height: (selected.toSlotInDay - selected.fromSlotInDay) * SLOT_HEIGHT,
            backgroundColor: 'color-mix(in oklch, var(--color-accent) 6%, transparent)',
          }}
          // Inert, so tapping inside the selection restarts it on the slot below.
          className="pointer-events-none absolute left-2 right-1 rounded-cell border-2 border-dashed border-accent"
        />
      ) : null}

      {selection.mode === 'range' ? (
        <button
          type="button"
          onClick={() => {
            openCreate({
              startSlot: dayStart + selection.fromSlotInDay,
              endSlot: dayStart + selection.toSlotInDay,
            });
            onFlowStarted();
          }}
          style={{ top: selection.fromSlotInDay * SLOT_HEIGHT + 8 }}
          className="absolute right-2 rounded-control bg-accent px-3 py-2 text-label font-semibold text-accent-fg shadow-pill"
        >
          {de.timeline.assignRange(
            formatTimeRange(
              dayStart + selection.fromSlotInDay,
              dayStart + selection.toSlotInDay,
            ),
          )}
        </button>
      ) : null}
    </div>
  );
}
